import { html, css } from "lit-element";
import { LitElement } from "@dreamworld/pwa-helpers/lit-element.js";
import { DwIcon } from '@dreamworld/dw-icon/dw-icon';
import { store } from '../../store.js';
import { connect } from '@dreamworld/pwa-helpers/connect-mixin.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { sharedStyles } from '../../theme/shared-styles.js';
import { openExternalLink } from '../open-external-link.js';
import KerikaLoginAndSignupMixin from './kerika-login-and-signup-mixin.js';
import typographyLiterals from "../../theme/typography-literals.js";
import i18next from '@dw/i18next-esm/index.js';
import localize from '@dw/pwa-helpers/localize.js';

import * as icons from './icons';
import * as analytics from '../../analytics/analytics.js';
import * as app from '../../redux/app';
import * as auth from '../../redux/auth';
import * as router from '../../redux/router';
import * as signup from '../../redux/signup';
import * as utils from '../../utils.js';
import * as linkedIn from '../../analytics/linkedin.js';

import '../common/kerika-button.js';
import '../kerika-icon.js';
import './kerika-signup-with-direct-email.js';
import './kerika-signin-with-direct-email.js';

DwIcon.addIcons({'google-logo': icons.googleLogo, 'box-logo': icons.boxLogo, 'microsoft-logo':icons.microsoftLogo });

/**
 * Provides view for login/signup into application with Google, Box, Microsoft or Direct email.
 */
export class KerikaLoginAndSignupView extends connect(store)(KerikaLoginAndSignupMixin(localize(i18next)(LitElement))) {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: flex;
          width: 100%;
          flex-direction: column;
          box-sizing: border-box;
        }

        [hidden] {
          display: none !important;
        }

        :host([mode="ACCEPTED_SIGNUP"]) p,
        :host([mode="EMAIL_NOT_AVAILABLE"]) p {
          text-align: center;
          margin: 8px 0px;
        }

        kerika-button[large]  {
          width: 100%;
          --kerika-button-height: 44px;
          margin-bottom: 24px;
        }

        kerika-button.resend-email {
          margin-top: 32px;
          margin-bottom: 0px;
        }

        kerika-button.google kerika-icon,
        kerika-button.box kerika-icon,
        kerika-button.microsoft kerika-icon {
          margin-right: 8px;
          height: 32px;
          position: absolute;
          top: 4px;
          left: 6px;
        }

        kerika-button.box kerika-icon,
        kerika-button.microsoft kerika-icon {
          left: 12px;
        }

        kerika-button.box {
          margin-bottom: 0px;
        }

        kerika-button.microsoft kerika-icon {
          height: 20px;
          top: 11px;
        }

        .account {
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          margin-top: 8px;
        }

        :host([mode="SIGNUP"]) .account {
          margin-top: 32px;
        }

        :host([mode="SIGNUP"][wide-layout]) .account {
          margin-top: 8px;
        }

        .account kerika-button {
          position: relative;
          text-transform: none;
          min-width: 60px;
          margin-bottom: 0px;
        }

        :host([language="hi"]) .account kerika-button {
          min-width: 96px;
          width: 96px;
        }

        .seprater {
          margin: 40px auto 24px auto;
          text-align: center;
          text-transform: uppercase;
          ${typographyLiterals.headline6};
        }

        .error-msg {
          text-align: center;
          color: var(--mdc-theme-error);
          margin-bottom: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .error-msg .msg {
          max-width: 350px;
          text-align: center;
        }

        :host([wide-layout]) .terms-of-use {
          margin-top: 24px;
          margin-bottom: 0px;
        }

        .terms-of-use {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 44px;
          margin-bottom: 24px;
        }

        .terms-of-use div {
          text-align: center;
        }

        .app-link {
          display: flex;
          flex-direction: column;
          margin-top: 24px;
        }

        .app-link .title {
          text-align: center;
        }

        .app-link .button {
          display: flex;
          justify-content: center;
          cursor: pointer;
        }

        .app-link .button img {
          width: 216px;
        }

        .app-link .button {
          margin-top: 12px;
        }

        :host([ios-device]) .app-link .button img {
          width: 190px;
        }

        :host([ios-device]) .app-link .button {
          margin-top: 24px;
        }

        @media (max-height: 792px) {
          .seprater {
            margin: 32px auto 16px auto;
          }
        }
    `];
  }

  static get properties() {
    return {
      /**
       * send verification email address.
       */
      _signupEmail: { type: String },

      /**
       * Hides signup option when its `true`.
       */
      _hideSignup: { type: Boolean },

      /**
       * re-send verification email process status.
       */
      _resendEmailstatus: { type: Boolean },

      /**
       * Represent tearm and condition page link.
       */
      _tearmAndConditionPageLink: { type: String },

      /**
       * `true` when wide layout.
       */
      _wideLayout: { type: Boolean, reflect: true, attribute: 'wide-layout' },

      /**
       * Read data from redux
       * Current device is iOS device or not.
       */
      _iOSDevice: { type: Boolean, reflect: true, attribute: 'ios-device'},

      /**
       * Read data from redux
       * Current device is desktop device or not.
       */
      _isDesktopDevice: { type: Boolean }
    }
  }

  constructor() {
    super();
    this.__redirectToTearmOfUsePage = this.__redirectToTearmOfUsePage.bind(this);
    this._onPostMessage = this._onPostMessage.bind(this);
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    window.addEventListener('message', this._onPostMessage);
  }

  render() {
    return html`
      <div class="error-msg" ?hidden=${!this.__getErrorMessage()}><div class="msg">${this.__getErrorMessage()}</div></div>

      ${this._wideLayout ? '': this._teamsOfUseTemplate}

      <!-- START: Google and Box and Microsoft login/signup -->
      <kerika-button ?hidden=${this.mode === 'ACCEPTED_SIGNUP' || this.mode === 'EMAIL_NOT_AVAILABLE'} class="google" outlined large @click=${this.__googleLoginSignup} .disabled=${this.inProgress}>
        <kerika-icon .size=${32} name="google-logo"></kerika-icon>
        ${i18next.t(this.mode === 'SIGNUP' ? 'login-and-signup:signup.buttons.google': 'login-and-signup:login.buttons.google')}
      </kerika-button>
      <kerika-button ?hidden=${this.mode === 'ACCEPTED_SIGNUP' || this.mode === 'EMAIL_NOT_AVAILABLE'} class="microsoft" outlined large @click=${this.__microsoftLoginSignup} .disabled=${this.inProgress}>
        <kerika-icon .size=${20} name='microsoft-logo'></kerika-icon>
        ${i18next.t(this.mode === 'SIGNUP' ? 'login-and-signup:signup.buttons.microsoft' : 'login-and-signup:login.buttons.microsoft')}
      </kerika-button>
      <kerika-button ?hidden=${this.mode === 'ACCEPTED_SIGNUP' || this.mode === 'EMAIL_NOT_AVAILABLE'} class="box" outlined large @click=${this.__boxLoginSignup} .disabled=${this.inProgress}>
        <kerika-icon .size=${32} name="box-logo"></kerika-icon>
        ${i18next.t(this.mode === 'SIGNUP' ? 'login-and-signup:signup.buttons.box': 'login-and-signup:login.buttons.box')}
      </kerika-button>

      <!-- END: Google and Box and Microsoft login/signup -->


      <div class="seprater" ?hidden=${this.mode === 'ACCEPTED_SIGNUP' || this.mode === 'EMAIL_NOT_AVAILABLE'}>${i18next.t('login-and-signup:seprater')}</div>
      ${this.mode === 'ACCEPTED_SIGNUP' ? html`
        <p>${i18next.t('login-and-signup:signup.signupAcceptNotification.description.1', { email: this._signupEmail })}</p>
        <p>${i18next.t('login-and-signup:signup.signupAcceptNotification.description.2')}</p>
        <p>${i18next.t('login-and-signup:signup.signupAcceptNotification.description.3')}</p>
        <kerika-button large class="resend-email" outlined .disabled=${this._resendEmailstatus === 'IN_PROGRESS'} @click=${this.__onResendSignupEmail}>${i18next.t('login-and-signup:signup.signupAcceptNotification.buttons.resend')}</kerika-button>
      `: this.mode === 'EMAIL_NOT_AVAILABLE' ? html`
          <p>${i18next.t('login-and-signup:emailNotAvailable.description', { service: this.service ? i18next.t(`service.${this.service}`): ''})}</p>
          <kerika-button large class="resend-email" outlined @click=${this.__openServiceSettingPage}>${this.service === 'BOX' || this.service === 'box' ? i18next.t(`login-and-signup:emailNotAvailable.buttons.box`): this.service === 'MICROSOFT' || this.service === 'microsoft' ? i18next.t(`login-and-signup:emailNotAvailable.buttons.microsoft`) : i18next.t(`login-and-signup:emailNotAvailable.buttons.google`)}</kerika-button>
      `: this.mode === 'SIGNUP'? html`
        <kerika-signup-with-direct-email></kerika-signup-with-direct-email>
      `: html`
        <kerika-signin-with-direct-email></kerika-signin-with-direct-email>
      `}

      ${this._wideLayout ? this._teamsOfUseTemplate: ''}
      ${this._pageModeTemplate}
      ${this._appLinkTemplate}
    `;
  }

  get _pageModeTemplate() {
    if(this._hideSignup || this.mode === 'ACCEPTED_SIGNUP' || this.mode === 'EMAIL_NOT_AVAILABLE') {
      return '';
    }

    return html`
      <div class="account">
        ${i18next.t(this.mode === 'SIGNUP' ? 'login-and-signup:signup.alreadyAccount': 'login-and-signup:login.notAccount')}
        <kerika-button .disabled=${this.inProgress} @click=${this.__changeMode} text-inherit>
          ${i18next.t(this.mode === 'SIGNUP' ? 'login-and-signup:login.buttons.login': 'login-and-signup:signup.buttons.signup')}
        </kerika-button>
      </div>
    `;
  }

  get _appLinkTemplate() {
    if(this._isDesktopDevice || this.installedApp || this.mode !== 'LOGIN') {
      return '';
    }

    return html`
      <div class="app-link">
        <div class="title">${i18next.t('login-and-signup:appLink.title')}</div>
        <div class="button" @click=${this.__redirectOnKerikaApp}>
          ${this._iOSDevice ? html`
            <img src="data:image/png;base64,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"/>
          `: html`
            <img src="data:image/png;base64,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"/>
          `}
        </div>
      </div>
    `;
  }

  get _teamsOfUseTemplate() {
    if(this.mode !== 'SIGNUP') {
      return '';
    }
    return html`
      <div class="terms-of-use">
        <div>
          ${unsafeHTML(i18next.t('login-and-signup:signup.signupForm.buttons.termsOfUse'))}
        </div>
      </div>
    `;
  }

  updated(changeProps) {
    super.updated && super.updated(changeProps);
    if(changeProps.has('_wideLayout') || changeProps.has('mode') || changeProps.has('active') || changeProps.has('language')) {
      const button = this.renderRoot.querySelector('a.tearm-of-use-link');
      if(button) {
        button.removeEventListener('click', this.__redirectToTearmOfUsePage);
        button.addEventListener('click', this.__redirectToTearmOfUsePage);
      }
    }
  }

  __googleLoginSignup() {
    if(this.mode === 'SIGNUP') {
      this.__googleSignup();
      return;
    }
    this.__googleLogin();
  }

  __boxLoginSignup() {
    if(this.mode === 'SIGNUP') {
      this.__boxSignup();
      return;
    }
    this.__boxLogin();
  }

  __microsoftLoginSignup() {
    if(this.mode === 'SIGNUP') {
      this.__microsoftSignup();
      return;
    }
    this.__microsoftLogin();
  }

  /**
   * Invoked when user click on google login button.
   * Dispatch `login` action of auth with service as a `google`.
   * @private
   */
  __googleLogin() {
    store.dispatch(auth.actions.login({service: 'google'}));
    analytics.logEvent('auth initiated', {service: 'GOOGLE', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
  }

  /**
   * Invoked when user click on box login button.
   * Dispatch `login` action of auth with service as a `box`.
   * @private
   */
  __boxLogin() {
    store.dispatch(auth.actions.login({service: 'box'}));
    analytics.logEvent('auth initiated', {service: 'BOX', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
  }

  /**
   * Invoked when user click on microsoft login button.
   * Dispatch `login` action of auth with service as a `microsoft`.
   * @private
   */
  __microsoftLogin() {
    store.dispatch(auth.actions.login({service: 'microsoft'}));
    analytics.logEvent('auth initiated', {service: 'MICROSOFT', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
  }

  /**
   * Invoked when user click on google signup button.
   * Dispatch `signup` action of auth with service as a `google`.
   * @private
   */
  __googleSignup() {
    store.dispatch(auth.actions.signup({service: 'google'}));
    analytics.logEvent('auth initiated', {service: 'GOOGLE', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
    linkedIn.logEvent(6794698);
  }

  /**
   * Invoked when user click on google signup button.
   * Dispatch `signup` action of auth with service as a `box`.
   * @private
   */
  __boxSignup() {
    store.dispatch(auth.actions.signup({service: 'box'}));
    analytics.logEvent('auth initiated', {service: 'BOX', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
    linkedIn.logEvent(6794706);
  }

  /**
   * Invoked when user click on microsoft signup button.
   * Dispatch `signup` action of auth with service as a `box`.
   * @private
   */
  __microsoftSignup() {
    store.dispatch(auth.actions.signup({service: 'microsoft'}));
    analytics.logEvent('auth initiated', {service: 'MICROSOFT', trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
    linkedIn.logEvent(6794706);
  }

  __changeMode() {
    if(this.mode === 'SIGNUP') {
      this.__redirectToLoginPage();
      return;
    }
    this.__redirectToSignupPage();
  }

  _onPostMessage(e) {
    if(!this.embedLoginSignup) {
      return;
    }

    const type = e && e.data && e.data.type;
    if(type === 'LOGIN_SIGNUP_MODE_CHANGED' || type === 'USE_TEMPLATE') {
      this.dispatchEvent(new CustomEvent('reset-data', {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail: { }
      }));
    }
  }

  /**
   * Invoked when user click on sign up buttons.
   * Redirect to signup page.
   * @private
   */
  __redirectToSignupPage() {
    if(this._dialogName === 'AUTH') {
      router.actions.setHashQueryParams({'mode': 'signup'});
    } else {
      router.actions.setQueryParams({'mode': 'signup'});
    }
  }

  /**
   * Invoked when user click on app button.
   * Redirect to app.
   * @private
   */
  __redirectOnKerikaApp() {
    const opener = window.top || window.opener;
    if(opener && this.embedLoginSignup) {
      opener.postMessage({
        type: 'APP_LINK_OPEN',
        url: this._appLink
      }, "*");
      return;
    }
    openExternalLink({href: this._appLink});
  }

  __redirectToTearmOfUsePage() {
    const opener = window.top || window.opener;
    if(opener && this.embedLoginSignup) {
      opener.postMessage({
        type: 'TEARM_OF_USE_PAGE_OPEN',
        url: this._tearmAndConditionPageLink
      }, "*");
      return;
    }
    openExternalLink({href: this._tearmAndConditionPageLink});
  }

  /**
   * Open service setting page.
   * @private
   */
  __openServiceSettingPage() {
    openExternalLink({ href: this.service === 'BOX' || this.service === 'box' ? 'https://app.box.com/settings': this.service === 'MICROSOFT' || this.service === 'microsoft' ? 'https://account.microsoft.com/profile':  'https://myaccount.google.com/personal-info'});
  }

  __onResendSignupEmail() {
    analytics.logEvent('signup resend confirmation email', {trigger: router.selectors.signupLoginPageOpenTrigger(store.getState())}, { trackReferrer: true });
    store.dispatch(auth.actions.resendVerificationEmail());
  }

  /**
   * Invoked when user click on login buttons.
   * Redirect to login page.
   * @private
   */
  __redirectToLoginPage() {
    if(this._dialogName === 'AUTH') {
      router.actions.setHashQueryParams({'mode': 'login'});
    } else {
      router.actions.setQueryParams({'mode': 'login'});
    }
  }

  /**
   * @returns {String} error message for login/signup failed.
   * @private
   */
  __getErrorMessage() {
    return this.mode === 'SIGNUP' ? this.__getSignupErrorMessage(): this.__getLoginErrorMessage();
  }

  /**
   * @returns {String} error message for login failed.
   * @private
   */
  __getLoginErrorMessage() {
    if(!this.error || this.error == 'USER_NOT_FOUND' || this.error === 'DOMAIN_IS_INVALID' || this.error == 'INVALID_PASSWORD' || this.error === 'LOCKED_OUT' || this.error == 'CANCELED' || this.error == 'EMAIL_NOT_AVAILABLE' || (this.error == 'BLACKLIST_DOMAIN_NOT_ALLOWED' && this.service === 'kerika')) {
      return '';
    }

    if(this.error === 'BLACKLIST_DOMAIN_NOT_ALLOWED') {
      return i18next.t('login-and-signup:error.BLACKLIST_DOMAIN_NOT_ALLOWED_FOR_THIRD_PARTY_LOGIN', { service: i18next.t(`service.${this.service}`) });
    }


    if(i18next.exists(`login-and-signup:error.${this.error}`)) {
      return i18next.t(`login-and-signup:error.${this.error}`, { service: i18next.t(`service.${this.service}`) });
    }

    return i18next.t(`login-and-signup:error.UNKNOWN`);
  }

  /**
   * @returns {String} error message for signup failed.
   * @private
   */
  __getSignupErrorMessage() {
    if(!this.error || this.error == 'CANCELED' || this.error == 'EMAIL_NOT_AVAILABLE' || this.error == 'DOMAIN_IS_INVALID' || (this.error == 'BLACKLIST_DOMAIN_NOT_ALLOWED' && this.service === 'kerika')) {
      return '';
    }

    if(this.error === 'BLACKLIST_DOMAIN_NOT_ALLOWED') {
      return i18next.t('login-and-signup:error.BLACKLIST_DOMAIN_NOT_ALLOWED_FOR_THIRD_PARTY_LOGIN', { service: i18next.t(`service.${this.service}`) });
    }

    if(i18next.exists(`login-and-signup:error.${this.error}`)) {
      return i18next.t(`login-and-signup:error.${this.error}`, { service: i18next.t(`service.${this.service}`) });
    }

    return i18next.t(`login-and-signup:error.UNKNOWN`);
  }

  stateChanged(state) {
    this._wideLayout = app.selectors.wideLayout(state);
    this._isDesktopDevice = app.selectors.isDesktopDevice(state);
    this._dialogName = router.selectors.dialogName(state);
    this._resendEmailstatus = auth.selectors.resendVerificationEmailStatus(state);
    const user = auth.selectors.signupUserInfo(state);
    this._signupEmail = user && user.email;

    const config  = app.selectors.config(state);
    this._websiteBaseUrl = config.websiteBaseUrl;
    this._tearmAndConditionPageLink = `${this._websiteBaseUrl}/terms-of-use.html`;
    
    this._iOSDevice = app.selectors.isIOSDevice(state);
    let appLinkConfig = config.appLink || {};
    this._appLink = this._iOSDevice ? appLinkConfig.ios : appLinkConfig.android;

    this._hideSignup = !!router.selectors.page(state).params['hide-signup'] || !signup.selectors.shouldShowSignupFeature(state);
  }

  disconnectedCallback() {
    window.removeEventListener('message', this._onPostMessage);
    super.disconnectedCallback && super.disconnectedCallback();
  }
}

customElements.define('kerika-login-and-signup-view', KerikaLoginAndSignupView);